import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigationState } from '../../navigation';
import { getSerializedTableCellKey, useSetHoveredCommentNode } from '../../state';
import { UserAvatarStack } from '../Users';
import { CommentAgentIndicator } from './CommentAgentIndicator';
import { CommentMessage } from './CommentMessage';
import styles from './Comments.module.css';
import { CommentThread, CommentThreadSkeleton } from './CommentThread';

export interface CommentCardProps {
    /* The space that the comment is in. */
    space: api.Space;

    /* The change request that the comment is in. */
    changeRequest: api.ChangeRequest | null;

    /* The comment to display. */
    comment: api.Comment;

    /* Show in which page this comment is located */
    showContextLink?: boolean;

    /* Whether the comment is active. */
    isSelected?: boolean;

    /* Callback when the card is pressed. */
    onPress?: (comment: api.Comment) => void;

    /* Additional className to apply to the card. */
    className?: string;
}

/**
 * A comment card can get highlighted orange if a node is in the URL. In practice, this should happen e.g. when the "2 comments" comment button is clicked with the sidepanel open.
 * This way, we visually convey which comments in the sidepanel are on the clicked node.
 */
function shouldHighlightCommentCard(comment: api.Comment, screen: NavigationScreenState) {
    if (screen.type !== 'space' || screen.sideSheet?.type !== 'comments') {
        return false;
    }

    // We never want to highlight the CommentCard within a popover (preview mode)
    if (screen.sideSheet?.context === 'preview') {
        return false;
    }

    // If the current node target is 'page', we highlight the CommentCard with comment.target.node.id === 'null'
    if (comment.target.node?.id === 'null' && screen.sideSheet.node === 'page') {
        return true;
    }

    // If the current node is in the URL and there is no comment in isolation mode (`comment` is undefined), we highlight
    if (
        screen.sideSheet.node === comment.target.node?.id &&
        !('comment' in screen.sideSheet) &&
        !screen.sideSheet.tableCell
    ) {
        return true;
    }
}

/**
 * A card that displays a comment, its replies (if any), and a reply input when the comment is active.
 */
export function CommentCard(props: CommentCardProps) {
    const {
        space,
        changeRequest,
        comment,
        showContextLink,
        isSelected = false,
        onPress,
        className,
    } = props;

    // Highlight the block when hovering the comment
    const setHoveredCommentNode = useSetHoveredCommentNode();
    const { screen } = useNavigationState();
    const shouldHighlight = shouldHighlightCommentCard(comment, screen);
    const targetIsTableCell = comment.target.tableCell !== undefined;

    const handleMouseEnterComment = useEventCallback(() => {
        if (comment.target.node) {
            return setHoveredCommentNode.highlight(
                targetIsTableCell
                    ? getSerializedTableCellKey(comment.target.tableCell) || null
                    : comment.target.node.id,
                {
                    highlighting: true,
                    postButton: false,
                }
            );
        }
    });

    const handleMouseLeaveComment = useEventCallback(() => {
        if (comment.target.node) {
            return setHoveredCommentNode.highlight(
                targetIsTableCell
                    ? getSerializedTableCellKey(comment.target.tableCell) || null
                    : comment.target.node.id,
                {
                    highlighting: false,
                    postButton: false,
                }
            );
        }
    });

    return (
        /** biome-ignore lint/a11y/noStaticElementInteractions: Todo: we should use AtomButton. That interferes with the nested BasicEditor in the CommentInput & CommentMessage, as parent type="button" elements hijack the spacebar and click events. */
        /** biome-ignore lint/a11y/useKeyWithClickEvents: see above */
        <div
            className={clsx(
                'flex w-full flex-col gap-2 p-3',
                onPress && 'cursor-pointer',
                styles.commentCard,
                className,
                shouldHighlight ? 'bg-[var(--sp-color-comments-block-bg)]' : 'bg-transparent'
            )}
            onClick={() => onPress?.(comment)}
            onMouseEnter={handleMouseEnterComment}
            onMouseLeave={handleMouseLeaveComment}
        >
            {/* The comment and its replies */}
            <div className="group/thread relative flex w-full flex-col">
                <CommentMessage
                    comment={comment}
                    isSelected={isSelected}
                    showContextLink={showContextLink}
                />
                {isSelected ? (
                    <React.Suspense fallback={<CommentThreadSkeleton count={comment.replies} />}>
                        <CommentThread comment={comment} />
                    </React.Suspense>
                ) : null}
            </div>

            {changeRequest && isSelected ? (
                <CommentAgentIndicator
                    spaceId={space.id}
                    changeRequestId={changeRequest.id}
                    commentId={comment.id}
                />
            ) : null}

            {comment.replies > 0 && !isSelected && onPress ? (
                <CommentDisplayRepliesButton
                    comment={comment}
                    isSelected={isSelected}
                    onPress={onPress}
                />
            ) : null}
        </div>
    );
}

function CommentDisplayRepliesButton(props: {
    comment: api.Comment;
    isSelected: boolean;
    onPress: (comment: api.Comment) => void;
}) {
    const { comment, isSelected, onPress } = props;

    const replyUsers = React.useMemo(() => {
        return comment.repliers.map((replier) => replier.user);
    }, [comment]);

    const label = `${isSelected ? 'Hide' : 'Show'} ${comment.replies} ${intlPluralizeString({
        count: comment.replies,
        singular: 'reply',
        plural: 'replies',
    })}`;

    return (
        <div className={clsx('relative pl-7', styles.showElbow)}>
            <Button
                kind="blank"
                size="small"
                label={label}
                className="relative z-10 flex w-fit gap-2 hover:bg-standout-on-base hover:text-secondary"
                onPress={() => {
                    onPress(comment);
                }}
                aria-label={label}
                trailingIcon={<Icon.ChevronRight />}
                leadingIcon={<UserAvatarStack users={replyUsers} size="xs" max={3} />}
            />
        </div>
    );
}
